import {AGENCY_KEYS} from "@/api/agencyApi";
import {useUpdateCreditsCache} from "@/api/companyApi";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {useMutation} from "@tanstack/react-query";
import {useState} from "react";

const useManageCredits = ({
  agencyId,
  companyId,
  credits,
  influencerFees,
  onConfirm,
  onSuccess,
  type,
}: {
  agencyId?: number;
  companyId?: number;
  credits: number;
  influencerFees: {pro?: number | null; regular?: number | null};
  onConfirm?(newBalance: number): void;
  onSuccess(): void;
  type: "add" | "remove";
}) => {
  const [inputValue, setInputValue] = useState<number | null>(0);
  const [isAdvancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState<"pro" | "regular">();
  const updateCreditsCache = useUpdateCreditsCache({agencyId, companyId});

  const isAddMode = type === "add";
  const selectedFeeValue =
    (selectedFee === "regular" ? influencerFees.regular : selectedFee === "pro" ? influencerFees.pro : 0) ?? 0;
  const modifiedCredits = +((inputValue ?? 0) + (selectedFeeValue / 100) * (inputValue ?? 0)).toFixed(2);
  const newBalance = credits + (modifiedCredits ? modifiedCredits * (isAddMode ? 1 : -1) : 0);

  const {mutate: patchCredits, isPending} = useMutation({
    mutationKey: AGENCY_KEYS.mAgency(agencyId!),
    mutationFn: (balance: number) => client.patch(AGENCY_KEYS.mAgency(agencyId!)[0], {credits: balance}),
    onSuccess: (_, balance) => {
      capturePostHogEvent(PostHogEvents.CreditsModified, {
        action: type,
        amount: inputValue,
        feeType: selectedFee,
        newBalance: balance,
      });
      updateCreditsCache(balance - credits);
      onSuccess();
    },
  });

  return {
    confirm: () => {
      if (!onConfirm) {
        patchCredits(newBalance);
        return;
      }

      onConfirm(newBalance);
      onSuccess();
    },
    inputValue,
    isAddMode,
    isAdvancedOptionsOpen,
    isConfirmDisabled: !inputValue || newBalance < 0,
    isPending: !onConfirm && isPending,
    newBalance,
    selectedFee,
    setAdvancedOptionsOpen,
    setInputValue,
    setSelectedFee,
  };
};

export type TManageCredits = ReturnType<typeof useManageCredits>;

export default useManageCredits;
